/* eslint-disable i18next/no-literal-string */
import { useState } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";

/**
 * Ported from bouncieweb's `LandingPageComponents/TuroVideoSection.js` (plus the
 * `VideoPlayer` / `VideoOverlay` and `Bento/BentoComponents` `GridItem` it composed).
 *
 * A blueLight3 bento tile: copy on one side, a click-to-play marketing video on
 * the other (stacked below lg, side-by-side from lg).
 *
 * Two deviations from the original, both matching the existing
 * `FamilyMarketingVideos` port:
 *   - The media id came from Redux `config.wistiaMediaIds.turoHost`, which this
 *     app has no store for. It is hardcoded from bouncieweb's `config/dev.js`.
 *   - `react-player` rendered Wistia's own iframe player. A native <video>
 *     against Wistia's `/embed/medias/<id>.bin` is rejected (HTTP 406), so the
 *     iframe embed is loaded on play instead.
 *
 * The original used `DefaultVideoOverlay` with no `title` and
 * `needsLessBottomMargin` (mb 24px instead of 56px), so the overlay is just the
 * "Watch video" pill.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   md 768px -> md:  lg 1024px -> lg:  xlg 1280px -> xl:
 */

// config/dev.js -> wistiaMediaIds.turoHost
const TURO_HOST_WISTIA_ID = "lc69k3ut53";

const wistiaIframeSrc = (id: string) =>
  `https://fast.wistia.net/embed/iframe/${id}?seo=false&videoFoam=false&autoPlay=1&playsinline=1`;

export default function TuroVideoSection() {
  const [started, setStarted] = useState(false);

  return (
    // Section (GridItem with padding stripped at every breakpoint)
    <div
      className="relative flex flex-col overflow-hidden rounded-[20px] p-0 lg:flex-row"
      style={{ background: colors.blueLight3 }}
    >
      {/* TextArea */}
      <div
        className={cn(
          "px-6 py-8",
          "lg:flex lg:w-1/2 lg:flex-col lg:justify-center lg:px-8 lg:py-0 lg:text-[18px]",
        )}
      >
        {/* BentoGridH2Large */}
        <h2
          className={cn(
            "font-display relative pr-8 leading-[1.15em] font-extrabold",
            "text-[9vw] min-[431px]:text-[6vw] sm:text-[33px] lg:text-[38px] xl:text-[40px]",
          )}
        >
          Smarter Hosting with Bouncie
        </h2>
        <p className="mb-0">
          See how Bouncie gives Turo hosts real-time vehicle tracking, trip
          insights, and peace of mind every time your car hits the road.
        </p>
      </div>

      {/* VideoPositioner */}
      <div className="relative w-full lg:w-1/2">
        {/* aspectRatio 400x225 */}
        <div className="relative aspect-[400/225] w-full overflow-hidden bg-[#1F2A3F]">
          {started ? (
            <iframe
              src={wistiaIframeSrc(TURO_HOST_WISTIA_ID)}
              title="Smarter Hosting with Bouncie"
              allow="autoplay; fullscreen"
              allowFullScreen
              className="absolute inset-0 h-full w-full border-0"
            />
          ) : (
            // DefaultVideoOverlay (no title, needsLessBottomMargin)
            <button data-sid="turo-video-start"
              type="button"
              onClick={() => setStarted(true)}
              className="absolute inset-0 flex cursor-pointer flex-col items-start justify-end border-none bg-transparent p-0 text-left"
            >
              <span
                className="mt-1 mb-6 flex items-center justify-center rounded-r px-12 py-2 backdrop-blur-[10px] sm:mt-2"
                style={{ backgroundColor: "rgba(30, 105, 242, 0.8)" }}
              >
                <span className="mr-2 text-[18px] font-medium text-white">
                  Watch video
                </span>
                <span className="w-8">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M6 4.5v15l13-7.5-13-7.5z" fill="#fff" />
                  </svg>
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
